import { Box, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import mergeRefs from 'merge-refs';
import React, { ReactElement, useContext, useMemo } from 'react';
import {
    AppFormikTextField,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import CompletedModal, {
    inferCompletedModal,
} from '../../single-fix-packet/components/CompletedModal';
import {
    FormikPropsFixPacketValues,
    NotExpectedMeta,
} from '../NotExpectedPage';
import { useLocation, useNavigate } from 'react-router-dom';
import ReworkBulkModal, { inferDocumentReworkModal } from './BulkReworkModal';
import { RawData } from '../../listing-packets/components/VirtualizedTable';
import { useNetworkState } from 'react-use';
import { Toast } from 'shared/helpers';

const Footer: React.FC<SinglePacketFooter> = (props): ReactElement => {
    const {
        onSave,
        onSubmit,
        onBack,
        onFailValidation,
        isLoading,
        innerModalCompletedRef,
        canCategorize,
        unexpectedType,
        updateGroup,
        allowRevalidation,
        allowRerun,
        allowRerunGrouping,
        allowRerunDuplication,
        onModalChanged,
        isActiveReworks,
    } = props;
    const { validateForm, setFieldTouched, values, setFieldValue } =
        useFormikContext<FormikPropsFixPacketValues>();
    const NotExpectedMetaProps = useContext(NotExpectedMeta);
    const innerModalDocumentBulkReworkRef =
        React.useRef<inferDocumentReworkModal | null>(null);
    const theme = useTheme();
    const match = useMediaQuery(theme.breakpoints.up('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const defaultState = location.state as RawData;
    const currentIdx = useMemo(
        () =>
            window.history.state &&
            typeof window.history.state.idx !== 'undefined'
                ? window.history.state.idx
                : null,
        []
    );
    const isPrimaryRoute = useMemo(
        () => typeof currentIdx === 'number' && currentIdx === 0,
        [currentIdx]
    );
    const clientId = useMemo(
        () =>
            defaultState && defaultState.clientId ? defaultState.clientId : '',
        [defaultState]
    );
    /** @constant {string} - clientId from action uuid in packet data */
    const packetId = useMemo(
        () =>
            defaultState && defaultState.actions && defaultState.actions.uuid
                ? defaultState.actions.uuid
                : '',
        [defaultState]
    );
    const isDuplicateDocumentsFound = useMemo(
        () =>
            defaultState &&
            typeof defaultState.isDuplicateDocumentsFound === 'boolean'
                ? defaultState.isDuplicateDocumentsFound
                : false,
        [location.state]
    );

    const showDuplicateDocuments = useMemo(
        () => (defaultState ? defaultState.showDuplicateDocuments : false),
        [location.state]
    );

    const isEscalation = useMemo(
        () => defaultState && defaultState.packetStatus === 'Escalated',
        [defaultState]
    );

    const hideSentForReworkFully = useMemo(
        () =>
            defaultState &&
            defaultState.packetStatus !== 'Fully Duplication' &&
            defaultState.packetStatus !== 'Partial Duplication' &&
            defaultState.packetStatus !== 'Duplicate - New docs'
                ? defaultState.hideSentForReworkFully
                : defaultState &&
                  (defaultState.packetStatus === 'Fully Duplication' ||
                      defaultState.packetStatus === 'Partial Duplication' ||
                      defaultState.packetStatus === 'Duplicate - New docs'),
        [location.state]
    );
    const isNotSendToRework = useMemo(
        () =>
            defaultState &&
            ((defaultState.packetStatus === 'Not Expected' &&
                ((canCategorize === false &&
                    updateGroup === false &&
                    unexpectedType === false &&
                    allowRevalidation === false &&
                    allowRerun === false) ||
                    unexpectedType === true ||
                    (isDuplicateDocumentsFound && showDuplicateDocuments))) ||
                defaultState.packetStatus === 'Fully Duplication' ||
                defaultState.packetStatus === 'Partial Duplication' ||
                defaultState.packetStatus === 'Duplicate - New docs'),
        [
            allowRerun,
            allowRevalidation,
            canCategorize,
            defaultState,
            isDuplicateDocumentsFound,
            showDuplicateDocuments,
            unexpectedType,
            updateGroup,
        ]
    );
    const completedModalRef = React.useRef<inferCompletedModal | null>(null);
    const isStateAvailable = useMemo(
        () => typeof location.state === 'object' && location.state !== null,
        [location.state]
    );

    const allowExplicitRerun = useMemo(
        () =>
            canCategorize === false &&
            updateGroup === false &&
            unexpectedType === false &&
            allowRevalidation === false &&
            (allowRerun === true || allowRerunDuplication === true),
        [
            allowRerun,
            allowRerunDuplication,
            allowRevalidation,
            canCategorize,
            unexpectedType,
            updateGroup,
        ]
    );
    /**
     * handle action save to open confirmation modal
     */
    const actionOnSaveChangesToOpenModal = React.useCallback(async () => {
        if (typeof onFailValidation === 'function') {
            onFailValidation();
        }
        const validate = await validateForm()
            .then((response) => {
                if (response && Object.keys(response).includes('documents')) {
                    if (
                        Array.isArray(response.documents) &&
                        response.documents.length > 0
                    ) {
                        if (updateGroup) {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].groups`,
                                    true
                                );
                            });
                        } else {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].category`,
                                    true
                                );
                            });
                        }
                    }
                    return false;
                }

                return true;
            })
            .catch(() => false);
        if (validate && typeof onSubmit === 'function') {
            onSubmit();
        }
    }, [
        onFailValidation,
        onSubmit,
        setFieldTouched,
        updateGroup,
        validateForm,
    ]);

    const handleOnSubmitType = React.useCallback(() => {
        if (
            unexpectedType ||
            allowRevalidation ||
            allowRerun ||
            (allowRerunDuplication && allowExplicitRerun) ||
            allowRerunGrouping
        ) {
            if (typeof onSubmit === 'function') {
                onSubmit();
            }
        } else {
            actionOnSaveChangesToOpenModal();
        }
    }, [
        actionOnSaveChangesToOpenModal,
        allowExplicitRerun,
        allowRerun,
        allowRerunDuplication,
        allowRerunGrouping,
        allowRevalidation,
        onSubmit,
        unexpectedType,
    ]);
    const handleFocusButtonSendRework = React.useCallback(() => {
        setFieldValue('isOpenToSendRework', true);
    }, [setFieldValue]);
    const handleBlurButtonSendRework = React.useCallback(() => {
        setFieldValue('isOpenToSendRework', false);
    }, [setFieldValue]);

    const { online } = useNetworkState();
    const handleSendToRework = React.useCallback(async () => {
        if (online === false) {
            Toast.showErrorMessage('No internet connection, Please try again!');
            return;
        }
        if (typeof onFailValidation === 'function') onFailValidation();
        const validate = await validateForm()
            .then((response) => {
                if (response && Object.keys(response).includes('documents')) {
                    if (
                        Array.isArray(response.documents) &&
                        response.documents.length > 0 &&
                        response.documents.some(
                            (t: any) =>
                                typeof t === 'object' &&
                                Object.keys(t).filter(
                                    (d) =>
                                        d !== 'predefinedMessage' &&
                                        d !== 'statusMessage'
                                ).length > 0
                        )
                    ) {
                        if (updateGroup) {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].groups`,
                                    true
                                );
                            });
                        } else {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].category`,
                                    true
                                );
                            });
                        }
                        return false;
                    }
                    return true;
                }

                return true;
            })
            .catch(() => false);
        if (validate) innerModalDocumentBulkReworkRef.current?.setOpen();
    }, [onFailValidation, online, setFieldTouched, updateGroup, validateForm]);

    const handleBackWithinAvailableState = React.useCallback(() => {
        if (isStateAvailable && typeof onBack === 'function') {
            onBack();
        } else {
            NotExpectedMetaProps?.setActiveBackNavigation(true);
            if (isPrimaryRoute) {
                navigate('/manage-packets', { replace: true });
            } else {
                navigate(-1);
            }
        }
    }, [
        NotExpectedMetaProps,
        isPrimaryRoute,
        isStateAvailable,
        navigate,
        onBack,
    ]);
    return (
        <Box className="footer not-expected-footer">
            <Stack
                direction={match ? 'row' : 'column'}
                display="flex"
                flexGrow={1}
            >
                {(isNotSendToRework === false ||
                    hideSentForReworkFully === false) && (
                    <>
                        <AppFormikTextField
                            name="isOpenToSendRework"
                            inputProps={{
                                type: 'hidden',
                            }}
                            className="isOpenToSendRework"
                        />
                        {isEscalation === false && (
                            <AppPrimaryButton
                                loadingSize={22}
                                loadingText="PROCESSING"
                                disabled={isActiveReworks === false}
                                title="Send To Rework"
                                className="btn-small save-button"
                                onFocus={handleFocusButtonSendRework}
                                onBlur={handleBlurButtonSendRework}
                                onClick={handleSendToRework}
                            />
                        )}
                    </>
                )}
                <Box className="saving-not-expected-categorise">
                    {(canCategorize ||
                        unexpectedType ||
                        updateGroup ||
                        allowRevalidation ||
                        allowRerun ||
                        allowRerunDuplication ||
                        allowRerunGrouping) &&
                    isEscalation === false ? (
                        <Grid container spacing={2} className="categorise">
                            <Grid item xs={6} className="categorise-item">
                                <AppPrimaryButton
                                    onClick={onBack}
                                    title="Back"
                                    disabled={
                                        NotExpectedMetaProps?.isActiveBackNavigation
                                    }
                                    className="btn-small back-button"
                                />
                            </Grid>
                            <Grid item xs={6} className="categorise-item">
                                <AppPrimaryButton
                                    isLoading={isLoading}
                                    loadingSize={22}
                                    disabled={isLoading || isActiveReworks}
                                    loadingText="PROCESSING"
                                    title={
                                        allowExplicitRerun || allowRerunGrouping
                                            ? 'Run Packet'
                                            : 'Save changes'
                                    }
                                    className="btn-small save-button"
                                    onClick={handleOnSubmitType}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2} className="non-categorise">
                            <Grid item xs={12} className="non-categorise-item">
                                <AppPrimaryButton
                                    disabled={
                                        NotExpectedMetaProps?.isActiveBackNavigation
                                    }
                                    onClick={handleBackWithinAvailableState}
                                    title="Back"
                                    className="btn-small back-button"
                                />
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Stack>
            <ReworkBulkModal
                datepickerRework={values.datepickerRework}
                missingDocuments={values.missingDocuments}
                duplicationDocuments={values.duplicationDocuments}
                documents={values.documents}
                clientId={clientId}
                packetId={packetId}
                ref={innerModalDocumentBulkReworkRef}
                callbackOpenModal={() => {
                    // setReworkModal(true);
                    // // console.log('test');
                    if (typeof onModalChanged === 'function')
                        onModalChanged(true, false, false);
                }}
                callbackClosingModal={() => {
                    // setReworkModal(true);
                    // // console.log('test');
                    if (typeof onModalChanged === 'function')
                        onModalChanged(false, false, false);
                }}
                callbackSavingModal={() => {
                    // setReworkModal(true);
                    // // console.log('test');
                    if (typeof onModalChanged === 'function')
                        onModalChanged(false, true, true);
                }}
            />
            <CompletedModal
                ref={mergeRefs(innerModalCompletedRef, completedModalRef)}
                onHandleSubmit={onSave}
            />
        </Box>
    );
};

export default Footer;
